import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Stethoscope, Loader2, Eye, EyeOff, CheckCircle2, Tag, ChevronDown } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const GRADES = [
  "Foundation Year 1",
  "Foundation Year 2",
  "Core Trainee (CT1/2)",
  "Core Trainee (CT3-5)",
  "Specialty Trainee (ST1/2)",
  "Specialty Trainee (ST3-5)",
  "Specialty Trainee (ST6-8)",
  "Specialty Registrar",
  "Specialty Doctor",
  "Associate Specialist",
  "Staff Grade",
  "Consultant",
  "Locum Consultant",
  "GP Partner",
  "Salaried GP",
  "Clinical Fellow",
  "Senior Clinical Fellow",
  "Trust Doctor",
  "Other"
];

const RegisterPage = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    password: '',
    phone: '',
    currentGrade: '',
  });
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return;
    
    setCouponLoading(true);
    try {
      const response = await axios.post(`${API_URL}/api/coupon/validate-public`, 
        { code: couponCode.trim().toUpperCase() }
      );
      setAppliedCoupon(response.data);
      setCouponCode('');
      toast.success(`Coupon applied! ${response.data.percentage}% off`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid coupon code');
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setAppliedCoupon(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (formData.password.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    if (!formData.currentGrade) {
      toast.error('Please select your current grade');
      return;
    }
    if (!acceptedTerms) {
      toast.error('Please accept the Terms of Service and Privacy Policy');
      return;
    }
    setLoading(true);
    try {
      const referralSlug = localStorage.getItem('referral_slug') || null;
      const response = await axios.post(`${API_URL}/api/auth/register-checkout`, {
        email: formData.email,
        password: formData.password,
        full_name: formData.fullName,
        phone: formData.phone || null,
        current_grade: formData.currentGrade,
        origin_url: window.location.origin,
        coupon_code: appliedCoupon?.code || null,
        referral_slug: referralSlug
      });

      if (response.data.checkout_url) {
        // Clear referral slug after successful registration
        localStorage.removeItem('referral_slug');
        // Redirect to Stripe checkout
        window.location.href = response.data.checkout_url;
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Registration failed');
      setLoading(false);
    }
  };

  const benefits = [
    'Unlimited email notifications',
    'Filter by specialty, grade & location',
    'Access to all UK doctor jobs',
    'Cancel anytime, no questions asked',
    'Track up to 5 jobs simultaneously',
  ];

  const originalPrice = 9.99;
  const discountedPrice = appliedCoupon 
    ? (originalPrice * (1 - appliedCoupon.percentage / 100)).toFixed(2)
    : originalPrice;

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Panel - Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          <div className="mb-8">
            <Link to="/" className="flex items-center gap-2 mb-8" data-testid="logo-link">
              <div className="w-10 h-10 rounded-lg bg-[#00A651] flex items-center justify-center">
                <Stethoscope className="w-6 h-6 text-white" />
              </div>
              <span className="font-bold text-xl text-slate-900">Trac Jobs Alert</span>
            </Link>
            <h1 className="text-3xl font-bold text-slate-900 mb-2">Create your account</h1>
            <p className="text-slate-600">Start receiving job alerts in minutes</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="fullName" className="text-slate-700 mb-2 block">Full name</Label>
              <Input
                id="fullName"
                name="fullName"
                type="text"
                value={formData.fullName}
                onChange={handleChange}
                placeholder="Dr. John Smith"
                required
                className="h-12"
                data-testid="fullname-input"
              />
            </div>

            <div>
              <Label htmlFor="email" className="text-slate-700 mb-2 block">Email address</Label>
              <Input
                id="email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="Enter your email"
                required
                className="h-12"
                data-testid="email-input"
              />
            </div>

            <div>
              <Label htmlFor="currentGrade" className="text-slate-700 mb-2 block">Current Grade</Label>
              <div className="relative">
                <select
                  id="currentGrade"
                  name="currentGrade"
                  value={formData.currentGrade}
                  onChange={handleChange}
                  required
                  className="w-full h-12 px-3 pr-10 rounded-md border border-slate-200 bg-white text-slate-900 appearance-none focus:outline-none focus:ring-2 focus:ring-[#00A651] focus:border-transparent"
                  data-testid="grade-select"
                >
                  <option value="">Select your current grade</option>
                  {GRADES.map((grade) => (
                    <option key={grade} value={grade}>{grade}</option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400 pointer-events-none" />
              </div>
            </div>

            <div>
              <Label htmlFor="phone" className="text-slate-700 mb-2 block">Phone number (optional)</Label>
              <Input
                id="phone"
                name="phone"
                type="tel"
                value={formData.phone}
                onChange={handleChange}
                placeholder="+44 7700 900000"
                className="h-12"
                data-testid="phone-input"
              />
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700 mb-2 block">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="Create a secure password"
                  required
                  minLength={6}
                  className="h-12 pr-10"
                  data-testid="password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Coupon Code Section */}
            <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
              <div className="flex items-center gap-2 mb-3">
                <Tag className="w-4 h-4 text-slate-500" />
                <span className="text-sm font-medium text-slate-700">Have a coupon code?</span>
              </div>
              
              {appliedCoupon ? (
                <div className="flex items-center justify-between p-3 bg-green-50 border border-green-200 rounded-lg">
                  <div>
                    <span className="font-mono font-bold text-green-700">{appliedCoupon.code}</span>
                    <span className="text-green-600 ml-2">({appliedCoupon.percentage}% off)</span>
                  </div>
                  <button
                    type="button"
                    onClick={removeCoupon}
                    className="text-red-500 hover:text-red-700 text-sm"
                  >
                    Remove
                  </button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <Input
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="ENTER CODE"
                    className="flex-1"
                    data-testid="coupon-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleApplyCoupon}
                    disabled={couponLoading || !couponCode.trim()}
                    data-testid="apply-coupon-btn"
                  >
                    {couponLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Apply'}
                  </Button>
                </div>
              )}

              {/* Price Display */}
              <div className="mt-3 pt-3 border-t border-slate-200">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Monthly subscription:</span>
                  {appliedCoupon ? (
                    <div>
                      <span className="line-through text-slate-400 mr-2">£{originalPrice}</span>
                      <span className="font-bold text-green-600">£{discountedPrice}/month</span>
                    </div>
                  ) : (
                    <span className="font-semibold">£{originalPrice}/month</span>
                  )}
                </div>
              </div>
            </div>

            {/* Terms Acceptance */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="terms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 w-4 h-4 text-[#00A651] border-slate-300 rounded focus:ring-[#00A651]"
                data-testid="terms-checkbox"
              />
              <label htmlFor="terms" className="text-sm text-slate-600">
                I agree to the{' '}
                <Link to="/terms" target="_blank" className="text-[#00A651] hover:underline">
                  Terms of Service
                </Link>{' '}
                and{' '}
                <Link to="/privacy" target="_blank" className="text-[#00A651] hover:underline">
                  Privacy Policy
                </Link>
              </label>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 bg-[#00A651] hover:bg-[#008C44] text-white rounded-full"
              data-testid="register-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  Proceeding to payment...
                </>
              ) : (
                `Continue to Payment - £${discountedPrice}`
              )}
            </Button>

            <p className="text-xs text-center text-slate-500">
              You'll be redirected to Stripe to complete your payment securely.
            </p>
          </form>

          <p className="mt-6 text-center text-slate-600">
            Already have an account?{' '}
            <Link to="/login" className="text-[#00A651] hover:underline font-medium" data-testid="login-link">
              Sign in
            </Link>
          </p>
        </div>
      </div>

      {/* Right Panel - Benefits */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#00A651] items-center justify-center p-12">
        <div className="max-w-md">
          <h2 className="text-3xl font-bold text-white mb-8">
            Your next doctor job is just an alert away
          </h2>
          <ul className="space-y-4">
            {benefits.map((benefit, index) => (
              <li key={index} className="flex items-center gap-3 text-white">
                <div className="w-6 h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-4 h-4" />
                </div>
                <span className="text-lg">{benefit}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default RegisterPage;
